import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Receipt, Search, Calendar, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtIDR = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

const monthNames = [
  'Januari','Februari','Maret','April','Mei','Juni',
  'Juli','Agustus','September','Oktober','November','Desember',
];

/**
 * CreateInvoiceModal — buat tagihan untuk 1 customer.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - onCreated: (invoice) => void  // callback setelah sukses
 *  - prefilledCustomer: { id, name, account_number, monthly_price, service_plan_name } | null
 *      → kalau diisi, customer search disembunyikan & locked
 */
const CreateInvoiceModal = ({ isOpen, onClose, onCreated, prefilledCustomer = null }) => {
  const today = new Date();
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [customer, setCustomer] = useState(prefilledCustomer);
  const [periodMonth, setPeriodMonth] = useState(today.getMonth() + 1);
  const [periodYear, setPeriodYear] = useState(today.getFullYear());
  const [baseAmount, setBaseAmount] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 7);
    return d.toISOString().split('T')[0];
  });
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastCreatedName, setLastCreatedName] = useState(null);
  const searchInputRef = useRef(null);

  // Reset form fields untuk Buat Lagi (chained create) — keep period + dueDate
  const resetForNextCustomer = () => {
    setCustomer(null);
    setSearch('');
    setSearchResults([]);
    setBaseAmount('');
    setDiscountAmount(0);
    setTaxAmount(0);
    setNotes('');
    // focus search input next tick
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  // Reset HANYA saat transisi closed -> open (mendeteksi via wasOpenRef).
  // Period/dueDate/lastCreatedName TIDAK di-reset oleh effect ini supaya bulk-create work properly.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      // Modal baru saja ditutup: bersihkan banner + customer untuk next open
      if (wasOpenRef.current) {
        setLastCreatedName(null);
        setCustomer(prefilledCustomer);
        setSearch('');
        setSearchResults([]);
        setBaseAmount(prefilledCustomer?.monthly_price ? String(prefilledCustomer.monthly_price) : '');
        setDiscountAmount(0);
        setTaxAmount(0);
        setNotes('');
        // Restore period defaults bila modal benar2 di-close
        const d = new Date();
        setPeriodMonth(d.getMonth() + 1);
        setPeriodYear(d.getFullYear());
        d.setDate(d.getDate() + 7);
        setDueDate(d.toISOString().split('T')[0]);
      }
      wasOpenRef.current = false;
      return;
    }
    // isOpen true: hanya inisialisasi prefilledCustomer-prefill saat first open
    if (!wasOpenRef.current) {
      wasOpenRef.current = true;
      if (prefilledCustomer) {
        setCustomer(prefilledCustomer);
        if (prefilledCustomer?.monthly_price) {
          setBaseAmount(String(prefilledCustomer.monthly_price));
        }
      }
    }
  }, [isOpen, prefilledCustomer]);

  // Search customers (debounced)
  useEffect(() => {
    if (prefilledCustomer || !search || search.length < 2) {
      setSearchResults([]);
      return;
    }
    const handler = setTimeout(async () => {
      setSearching(true);
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API_URL}/api/v2/customers`, {
          params: { search, limit: 10 },
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = r.data?.data || [];
        setSearchResults(data);
      } catch (e) {
        // silent
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [search, prefilledCustomer]);

  const total = Math.max(0, Number(baseAmount || 0) + Number(taxAmount || 0) - Number(discountAmount || 0));

  const handleSelectCustomer = (c) => {
    setCustomer(c);
    setSearch('');
    setSearchResults([]);
    if (c.monthly_price && !baseAmount) setBaseAmount(String(c.monthly_price));
  };

  const handleSubmit = async () => {
    if (!customer?.id) {
      toast.error('Pilih customer terlebih dahulu');
      return;
    }
    if (!baseAmount || Number(baseAmount) <= 0) {
      toast.error('Nominal harus lebih dari 0');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        customer_id: customer.id,
        customer_name: customer.name,
        account_number: customer.account_number || null,
        period_month: Number(periodMonth),
        period_year: Number(periodYear),
        base_amount: Number(baseAmount),
        tax_amount: Number(taxAmount || 0),
        discount_amount: Number(discountAmount || 0),
        total_amount: total,
        issue_date: today.toISOString().split('T')[0],
        due_date: dueDate,
        status: 'pending',
        notes: notes || null,
      };
      const r = await axios.post(`${API_URL}/api/v2/invoices`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      var createdInvoice = null;
      var createdSuccess = false;
      var createdName = customer?.name;
      if (r.data?.success) {
        createdInvoice = r.data.data;
        createdSuccess = true;
      } else {
        toast.error(r.data?.message || 'Gagal membuat tagihan');
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const fallback = e.response?.status === 409 ? 'Tagihan untuk periode ini sudah ada.' : 'Gagal membuat tagihan';
      toast.error(typeof detail === 'string' ? detail : fallback);
    } finally {
      setSubmitting(false);
    }
    // ----- POST-SUCCESS (di luar try-catch supaya error onCreated tidak misreport sebagai gagal POST) -----
    if (createdSuccess) {
      // Refresh parent list (errors di parent tidak akan trigger toast error)
      try { onCreated?.(createdInvoice); } catch (err) { console.warn('onCreated callback error:', err); }

      if (prefilledCustomer) {
        // Single-shot mode (dipanggil dari CustomerDetail) — close modal seperti dulu
        toast.success(`Tagihan berhasil dibuat untuk ${createdName}`);
        onClose?.();
      } else {
        // Bulk mode — keep modal open, reset form, show success toast with action "Tutup"
        setLastCreatedName(createdName);
        resetForNextCustomer();
        toast.success(`Tagihan dibuat untuk ${createdName}. Lanjut customer berikutnya?`, {
          duration: 6000,
          action: { label: 'Tutup', onClick: () => onClose?.() },
        });
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="create-invoice-modal">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/40 rounded-md">
              <Receipt className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Buat Tagihan Baru</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" data-testid="modal-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {/* Customer selector */}
          {prefilledCustomer ? (
            <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <div className="text-xs text-indigo-700 dark:text-indigo-300 font-medium mb-1">Customer</div>
              <div className="font-semibold text-slate-900 dark:text-slate-100">{prefilledCustomer.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {prefilledCustomer.account_number && <span className="font-mono mr-2">#{prefilledCustomer.account_number}</span>}
                {prefilledCustomer.service_plan_name}
              </div>
            </div>
          ) : (
            <div>
              {lastCreatedName && !prefilledCustomer && (
            <div className="mb-3 p-2.5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded text-xs text-emerald-800 dark:text-emerald-300" data-testid="last-created-banner">
              <span className="font-semibold">✓ Tagihan terakhir:</span> {lastCreatedName} — form sudah di-reset, silakan pilih customer berikutnya atau klik X untuk tutup.
            </div>
          )}
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Pilih Customer *</label>
              {customer ? (
                <div className="p-2.5 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate">{customer.name}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {customer.account_number && <span className="font-mono mr-2">#{customer.account_number}</span>}
                      {customer.service_plan_name || customer.pppoe_username}
                    </div>
                  </div>
                  <button onClick={() => setCustomer(null)} className="text-xs text-red-600 hover:underline ml-2 shrink-0" data-testid="clear-customer">Ganti</button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Cari nama, nomor akun, atau PPPoE..."
                    className="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="customer-search-input"
                    autoFocus
                  />
                  {searching && (
                    <Loader2 className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
                  )}
                  {searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {searchResults.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => handleSelectCustomer(c)}
                          className="w-full text-left p-2.5 hover:bg-slate-100 dark:hover:bg-slate-700 border-b border-slate-100 dark:border-slate-700 last:border-0"
                          data-testid={`customer-option-${c.id}`}
                        >
                          <div className="font-medium text-sm text-slate-900 dark:text-slate-100">{c.name}</div>
                          <div className="text-xs text-slate-500">
                            {c.account_number && <span className="font-mono mr-2">#{c.account_number}</span>}
                            {c.service_plan_name || c.pppoe_username || '-'}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Period */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Bulan *</label>
              <select
                value={periodMonth}
                onChange={(e) => setPeriodMonth(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800"
                data-testid="period-month"
              >
                {monthNames.map((n, i) => (
                  <option key={i+1} value={i+1}>{n}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Tahun *</label>
              <input
                type="number"
                value={periodYear}
                onChange={(e) => setPeriodYear(Number(e.target.value))}
                min={2020}
                max={2099}
                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800"
                data-testid="period-year"
              />
            </div>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Nominal Dasar *</label>
            <input
              type="number"
              value={baseAmount}
              onChange={(e) => setBaseAmount(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 font-mono"
              data-testid="base-amount"
            />
            {customer?.monthly_price && (
              <div className="text-xs text-slate-500 mt-1">
                Harga paket: {fmtIDR(customer.monthly_price)}{' '}
                <button
                  type="button"
                  onClick={() => setBaseAmount(String(customer.monthly_price))}
                  className="text-indigo-600 hover:underline ml-1"
                >
                  pakai
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Diskon</label>
              <input
                type="number"
                value={discountAmount}
                onChange={(e) => setDiscountAmount(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 font-mono"
                data-testid="discount-amount"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Pajak</label>
              <input
                type="number"
                value={taxAmount}
                onChange={(e) => setTaxAmount(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 font-mono"
                data-testid="tax-amount"
              />
            </div>
          </div>

          {/* Due date */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Jatuh Tempo *</label>
            <div className="relative">
              <Calendar className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800"
                data-testid="due-date"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Catatan (opsional)</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {[
                'Tagihan susulan',
                'Koreksi nominal',
                'Tagihan ekstra layanan',
                'Biaya pemasangan',
                'Penyesuaian harga',
              ].map((tpl) => (
                <button
                  key={tpl}
                  type="button"
                  onClick={() => setNotes((prev) => {
                    const isSusulan = tpl === 'Tagihan susulan';
                    const marker = '[SUSULAN] ';
                    if (isSusulan) {
                      const cleaned = (prev || '').replace(/^\[SUSULAN\]\s*/i, '');
                      const base = cleaned ? (cleaned.endsWith(' ') ? cleaned : cleaned + ' ') + tpl : tpl;
                      return marker + base;
                    }
                    return prev ? prev + (prev.endsWith(' ') ? '' : ' ') + tpl : tpl;
                  })}
                  className="text-[11px] px-2 py-1 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 transition-colors"
                  data-testid={`note-chip-${tpl.toLowerCase().replace(/ /g, "-")}`}
                >
                  + {tpl}
                </button>
              ))}
              {notes && (
                <button
                  type="button"
                  onClick={() => setNotes('')}
                  className="text-[11px] px-2 py-1 rounded-full border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                  data-testid="note-chip-clear"
                >
                  × Hapus
                </button>
              )}
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 resize-none"
              placeholder="Klik chip di atas atau ketik manual"
              data-testid="notes"
            />
          </div>

          {/* Total preview */}
          <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800 flex items-center justify-between">
            <span className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">Total Tagihan</span>
            <span className="text-lg font-bold text-emerald-700 dark:text-emerald-300 font-mono" data-testid="total-amount">{fmtIDR(total)}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
            data-testid="modal-cancel"
          >
            Batal
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !customer?.id || !baseAmount}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="modal-submit"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Buat Tagihan
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateInvoiceModal;
